import { useTheme } from '@nexelem/deprecated-theme';
import { generateKebabCase } from '@nexelem/utils';
import { isBefore } from 'date-fns';
import { NodeType, type QueueMultipleNode, type QueueTaskNode } from '../../types';
import type { generateQueues } from '../../utils';
import {
  getGanttDepsRelationPoints,
  getGanttQueueEdgeXInChart,
  getGraphElementById,
  getQueueElementById,
  type Point,
} from './utils';
import type { DependencyDetails } from '@nexelem/graphne-api';
import { GanttUtils } from '../../../utils';

type PolylineProps = {
  readonly ganttQueues: Array<ReturnType<typeof generateQueues>>;
  readonly targetTask: DependencyDetails;
  readonly startPoint: Point;
  readonly sourceTask: QueueTaskNode;
  readonly sourceQueueIndex: number;
  readonly leftOffset?: number;
};

const TASK_PADDING = 8;

export const Polyline = ({
  sourceQueueIndex,
  ganttQueues,
  targetTask,
  leftOffset,
  startPoint,
  sourceTask,
}: PolylineProps) => {
  const {
    client: { statuses },
    palette: { getColor },
  } = useTheme();
  const queueIndex = ganttQueues.findIndex((_) => _.id === targetTask.queueId);
  const queue = ganttQueues[queueIndex];
  const targetTaskIdStr = targetTask.taskId.toString();

  const taskCandidates = queue?.queueNodes
    ?.filter((_) => [NodeType.TASK, NodeType.MULTIPLE].includes(_.type))
    .filter((_) => _.id?.toString().includes(targetTaskIdStr)) as Array<QueueTaskNode | QueueMultipleNode>;

  if (taskCandidates?.length) {
    for (const taskCandidate of taskCandidates) {
      const finalTask =
        taskCandidate.type === NodeType.TASK
          ? taskCandidate
          : taskCandidate.overlappingNodes?.find((_) => _.id.toString().includes(targetTaskIdStr));

      if (finalTask?.type === NodeType.TASK) {
        const graphElementById = getGraphElementById(finalTask.id);
        // node exist in current scope
        if (graphElementById) {
          const endPoint = getGanttDepsRelationPoints(
            graphElementById,
            queueIndex,
            taskCandidate.nodeWidth,
            leftOffset,
          );

          if (endPoint && startPoint) {
            const shouldEndUseBottom = queueIndex > sourceQueueIndex;
            const shouldStartBottom = shouldEndUseBottom || queueIndex === sourceQueueIndex;
            const startYCord = shouldStartBottom ? startPoint.startYBottom : startPoint.startYTop;
            const endYCord = shouldEndUseBottom ? endPoint.startYTop : endPoint.startYBottom;
            const fullCords = [
              [startPoint.middleTask, startYCord + (shouldStartBottom ? -TASK_PADDING : TASK_PADDING)],
              [startPoint.middleTask, startYCord],
              [startPoint.middleTask, endYCord],
              [endPoint.middleTask, endYCord],
              [endPoint.middleTask, +endYCord + (shouldEndUseBottom ? TASK_PADDING : -TASK_PADDING)],
            ];

            return (
              <polyline
                fill="none"
                key={generateKebabCase(taskCandidate.id, taskCandidate.taskExecutionId)}
                points={fullCords.map((_) => _.join(',')).join(' ')}
                strokeLinejoin="round"
                strokeWidth="2"
                stroke={getColor(
                  statuses[GanttUtils.getTaskStatusColor(finalTask.status, finalTask.errors)].dotTextText,
                )}
              />
            );
          }
        }
      }
    }

    // Task is in gantt data but its DOM node is not mounted yet — skip queue-edge
    // fallback (endless lines). Parent remounts the graph after layout.
    return null;
  }
  // node don't exist in current date range / queue data
  const queueElement = targetTask.queueId && getQueueElementById(targetTask.queueId);
  // when queue is displayed
  if (queueElement) {
    const endPoint = getGanttDepsRelationPoints(queueElement, queueIndex, queueElement.clientWidth, leftOffset);
    if (endPoint && startPoint) {
      const isLeft = isBefore(new Date(targetTask.start || ''), sourceTask.startDate);
      const shouldEndUseBottom = queueIndex > sourceQueueIndex;
      const shouldStartBottom = shouldEndUseBottom || queueIndex === sourceQueueIndex;
      const startYCord = shouldStartBottom ? startPoint.startYBottom : startPoint.startYTop;
      const endYCord = shouldEndUseBottom ? endPoint.startYTop : endPoint.startYBottom;
      const xEndCord = getGanttQueueEdgeXInChart(queueElement, isLeft ? 'left' : 'right', leftOffset);
      const fullCords = [
        [startPoint.middleTask, startYCord + (shouldStartBottom ? -TASK_PADDING : TASK_PADDING)],
        [startPoint.middleTask, startYCord],
        [startPoint.middleTask, endYCord],
        [xEndCord, endYCord],
        [xEndCord, endYCord],
      ];

      return (
        <polyline
          fill="none"
          points={fullCords.map((_) => _.join(',')).join(' ')}
          stroke={getColor(statuses[GanttUtils.getTaskStatusColor(targetTask.status)].dotTextText)}
          strokeLinejoin="round"
          strokeWidth="2"
        />
      );
    }
  }

  return null;
};
